import { Org } from '../universal-nav/types';

/**
 * The three views the Org menu (and the Manager roles table) can be filtered to.
 * `all` shows every org; `my` only favorited orgs (plus the always-favorited
 * primary/top org); `hidden` only the orgs NOT favorited.
 */
export type OrgMenuFilterMode = 'all' | 'my' | 'hidden';

type OrgNode = Pick<Org, 'id' | 'name' | 'children'>;

/** The filter mode plus the two pieces of context every mode check needs. */
export interface OrgFilterContext {
  favoriteOrgIds: string[] | undefined;
  mode: OrgMenuFilterMode;
  primaryOrgId: string | undefined;
}

/** Whether `orgId` is in the user's `favoriteOrgIds` list. */
export function isOrgFavorited(
  orgId: string | undefined,
  favoriteOrgIds: string[] | undefined
): boolean {
  return !!orgId && !!favoriteOrgIds?.includes(orgId);
}

/**
 * Whether `org` counts as being "in My Orgs" — either explicitly favorited, or
 * the primary/top org, which is always in My Orgs and never user-togglable.
 */
export function isOrgInMyOrgs(
  org: Pick<Org, 'id'>,
  favoriteOrgIds: string[] | undefined,
  primaryOrgId?: string
): boolean {
  return (
    (!!org.id && org.id === primaryOrgId) ||
    isOrgFavorited(org.id, favoriteOrgIds)
  );
}

/** Whether `org` should be shown under the given filter mode. */
export function matchesOrgFilterMode(
  org: Pick<Org, 'id'>,
  context: OrgFilterContext
): boolean {
  const { favoriteOrgIds, mode, primaryOrgId } = context;

  if (mode === 'all') return true;

  const inMyOrgs = isOrgInMyOrgs(org, favoriteOrgIds, primaryOrgId);

  return mode === 'my' ? inMyOrgs : !inMyOrgs;
}

/**
 * Returns a NEW tree (no mutation of the input, unlike the old
 * `setOrgsVisibility`) containing only the orgs — at every depth — that match
 * the given filter mode, OR that have at least one descendant that does (an
 * ancestor is kept as the path needed to reach a matching descendant, the
 * same "keep a parent whose child is visible" property `setOrgsVisibility`
 * had — but computed fresh on every call instead of mutated onto the tree).
 */
export function filterOrgTreeByMode<T extends OrgNode>(
  orgs: T[] | undefined,
  context: OrgFilterContext
): T[] {
  if (!orgs?.length) return [];

  return orgs.reduce<T[]>((acc, org) => {
    const filteredChildren = filterOrgTreeByMode(
      org.children as T[] | undefined,
      context
    );
    const selfMatches = matchesOrgFilterMode(org, context);

    if (selfMatches || filteredChildren.length) {
      acc.push({ ...org, children: filteredChildren });
    }

    return acc;
  }, []);
}

/**
 * Returns a NEW tree containing only the orgs whose name matches `searchTerm`
 * (case-insensitive substring) OR that have a matching descendant. An org
 * whose own name matches keeps its full, unfiltered subtree — searching for a
 * parent org is expected to reveal all of its children.
 */
export function filterOrgTreeBySearchTerm<T extends OrgNode>(
  orgs: T[] | undefined,
  searchTerm: string
): T[] {
  const term = searchTerm.trim().toLowerCase();

  if (!orgs?.length) return [];
  if (!term) return orgs;

  return orgs.reduce<T[]>((acc, org) => {
    const nameMatches = (org.name || '').toLowerCase().includes(term);
    const matchedChildren = filterOrgTreeBySearchTerm(
      org.children as T[] | undefined,
      searchTerm
    );

    if (nameMatches || matchedChildren.length) {
      acc.push({
        ...org,
        children: nameMatches ? org.children : matchedChildren,
      });
    }

    return acc;
  }, []);
}

/** Total number of orgs in the tree, at every depth. */
export function countOrgsInTree(orgs: OrgNode[] | undefined): number {
  if (!orgs?.length) return 0;

  return orgs.reduce(
    (total, org) =>
      total + 1 + countOrgsInTree(org.children as OrgNode[] | undefined),
    0
  );
}

/** Number of orgs in the tree, at every depth, matching the given filter mode. */
export function countOrgsMatchingMode(
  orgs: OrgNode[] | undefined,
  context: OrgFilterContext
): number {
  if (!orgs?.length) return 0;

  return orgs.reduce((total, org) => {
    const selfMatches = matchesOrgFilterMode(org, context) ? 1 : 0;

    return (
      total +
      selfMatches +
      countOrgsMatchingMode(org.children as OrgNode[] | undefined, context)
    );
  }, 0);
}
